import {createReducer, on} from "@ngrx/store";
import {DoseEntity, DoseType} from "fitmed-models";

import * as PrescriptionDetailActions from './prescription-detail.actions'
import {hasValue} from "@shared/utils/helpers";
import {updateDoseFailure, updateDoseSuccess} from "./prescription-detail.actions";

export interface PrescriptionDetailState {
  // stores a reference for all the doses that have been loaded for the prescriptions
  doses: {
    // prescriptions currently loading
    loadingFor: string[];
    // prescriptions already checked (should not be loaded again)
    checked: string[]
    // the doses successfully loaded
    doses: DoseEntity[];
  }

  dosesUpdating: { doseType: DoseType, prescriptionId: string }[];

  //  the prescriptions that have already been checked in the past for outcomes to prevent re-querying for the data
  checkedPrescriptions: string[];
}

export const initialState: PrescriptionDetailState = {
  doses: {
    loadingFor: [],
    checked: [],
    doses: []
  },
  dosesUpdating: [],
  checkedPrescriptions: [],
}

export const prescriptionDetailReducer = createReducer(
  initialState,
  on(PrescriptionDetailActions.setPrescriptions, (state, {prescriptions}) => {
    let newP = []
    if (hasValue(prescriptions)) {
      newP = [...prescriptions].sort((a, b) => a.itemOrder - b.itemOrder)
    }

    return {
      ...state,
      prescriptions: newP
    }
  }),

  on(PrescriptionDetailActions.loadDoses, (state, {prescriptions}) => {
    let prescriptionsToLoadFor: string[] = [];
    if (hasValue(prescriptions)) {
      for (const prescription of prescriptions) {
        const alreadyLoading = state.doses.loadingFor.includes(prescription)
        const alreadyChecked = state.doses.checked.includes(prescription)

        if (!alreadyLoading && !alreadyChecked) {
          prescriptionsToLoadFor.push(prescription)
        }
      }
    }

    return {
      ...state,
      doses: {
        ...state.doses,
        loadingFor: [...state.doses.loadingFor, ...prescriptionsToLoadFor]
      }
    }
  }),
  on(PrescriptionDetailActions.loadDosesSuccess, (state, {doses, prescriptions}) => {
    const updateDoses = doses ?? [];
    const newLoading = [...state.doses.loadingFor].filter(e => !prescriptions.includes(e));
    let newDoses = [...state.doses.doses]

    for (const prescriptionId of prescriptions) {
      const dosesExistForPrescription = newDoses.filter(e => e.prescriptionId === prescriptionId).length !== 0
      // filter out existing doses if the prescription already has doses
      if (dosesExistForPrescription) {
        newDoses = newDoses.filter(e => e.prescriptionId !== prescriptionId)
      }
    }

    newDoses = [...newDoses, ...updateDoses]

    return {
      ...state,
      doses: {
        ...state.doses,
        doses: newDoses,
        checked: [...state.doses.checked, ...prescriptions],
        loadingFor: newLoading,
      }
    }
  }),
  on(PrescriptionDetailActions.loadDosesFailure, (state, {error, prescriptions}) => {
    const newLoading = [...state.doses.loadingFor].filter(e => !prescriptions.includes(e));

    return {
      ...state,
      doses: {
        ...state.doses,
        loadingFor: newLoading,
      }
    }
  }),

  on(PrescriptionDetailActions.updateDoseFailure, (state, { update }) => {
    const updatingSubject = {doseType: update.type, prescriptionId: update.prescriptionId};

    let dosesUpdating = [...state.dosesUpdating];
    const index = dosesUpdating.findIndex(e => e.doseType === updatingSubject.doseType && e.prescriptionId === updatingSubject.prescriptionId)
    if (index !== -1) {
      dosesUpdating = dosesUpdating.splice(index, 1)
    }

    return {
      ...state,
      dosesUpdating
    };
  }),

  on(PrescriptionDetailActions.updateDoseSuccess, (state, { dose }) => {
    if (!dose.id) {
      // Optionally handle cases where dose.id is undefined or null
      console.warn('DoseEntity received without an id:', dose);
      return state; // Or handle as per your requirements
    }

    const doseExists = state.doses.doses.some(existingDose => existingDose.id === dose.id);

    let newDoses: DoseEntity[] = []
    if (doseExists) {
      newDoses = state.doses.doses.map(existingDose =>
        existingDose.id === dose.id ? { ...existingDose, ...dose } : existingDose
      )
    } else {
      newDoses = [...state.doses.doses, dose]
    }

    const updatingSubject = {doseType: dose.type, prescriptionId: dose.prescriptionId};

    let dosesUpdating = [...state.dosesUpdating];
    const index = dosesUpdating.findIndex(e => e.doseType === updatingSubject.doseType && e.prescriptionId === updatingSubject.prescriptionId)
    if (index !== -1) {
      dosesUpdating = dosesUpdating.splice(index, 1)
    }

    return {
      ...state,
      doses: {
        ...state.doses,
        doses: newDoses
      },
      dosesUpdating
    };
  }),
)
